import { useState, useMemo } from "react";

export interface RefinanceInputs {
  // Current loan
  currentBalance: number;
  currentRate: number;
  currentTermRemaining: number; // Years remaining
  currentMonthlyPayment: number;

  // New loan
  newRate: number;
  newTerm: number;
  switchingCosts: number; // Discharge fees, application fees, etc.
  cashOut: number; // Additional amount to borrow
}

export interface RefinanceResults {
  // Current loan projections
  currentTotalInterest: number;
  currentTotalRepayments: number;
  currentMonthlyPayment: number;

  // New loan details
  newLoanAmount: number;
  newMonthlyPayment: number;
  newTotalInterest: number;
  newTotalRepayments: number;

  // Savings
  monthlyPaymentDifference: number;
  totalInterestSaved: number;
  netSavings: number;
  breakEvenMonths: number;
  yearlysSavings: number;
}

const defaultInputs: RefinanceInputs = {
  currentBalance: 450000,
  currentRate: 6.5,
  currentTermRemaining: 25,
  currentMonthlyPayment: 3040,
  newRate: 5.89,
  newTerm: 25,
  switchingCosts: 1500,
  cashOut: 0,
};

/**
 * Calculate monthly payment
 */
function calculateMonthlyPayment(principal: number, annualRate: number, years: number): number {
  const monthlyRate = annualRate / 100 / 12;
  const periods = years * 12;

  if (monthlyRate === 0) return principal / periods;

  return (
    (principal * (monthlyRate * Math.pow(1 + monthlyRate, periods))) /
    (Math.pow(1 + monthlyRate, periods) - 1)
  );
}

/**
 * Calculate total interest over loan term
 */
function calculateTotalInterest(principal: number, monthlyPayment: number, years: number): number {
  const totalPayments = monthlyPayment * years * 12;
  return totalPayments - principal;
}

/**
 * Refinance Calculator Hook
 */
export function useRefinanceCalculator() {
  const [inputs, setInputs] = useState<RefinanceInputs>(defaultInputs);

  const results = useMemo<RefinanceResults>(() => {
    const {
      currentBalance,
      currentRate,
      currentTermRemaining,
      newRate,
      newTerm,
      switchingCosts,
      cashOut,
    } = inputs;

    // Current loan calculations
    const currentMonthlyPayment = calculateMonthlyPayment(
      currentBalance,
      currentRate,
      currentTermRemaining
    );
    const currentTotalInterest = calculateTotalInterest(
      currentBalance,
      currentMonthlyPayment,
      currentTermRemaining
    );
    const currentTotalRepayments = currentBalance + currentTotalInterest;

    // New loan calculations
    const newLoanAmount = currentBalance + switchingCosts + cashOut;
    const newMonthlyPayment = calculateMonthlyPayment(newLoanAmount, newRate, newTerm);
    const newTotalInterest = calculateTotalInterest(newLoanAmount, newMonthlyPayment, newTerm);
    const newTotalRepayments = newLoanAmount + newTotalInterest;

    // Savings calculations
    const monthlyPaymentDifference = currentMonthlyPayment - newMonthlyPayment;
    const totalInterestSaved = currentTotalInterest - newTotalInterest;

    // Net savings = interest saved - switching costs - extra interest on cash out
    const netSavings = totalInterestSaved - switchingCosts;

    // Break even = switching costs / monthly savings
    const breakEvenMonths =
      monthlyPaymentDifference > 0 ? Math.ceil(switchingCosts / monthlyPaymentDifference) : 0;

    const yearlysSavings = monthlyPaymentDifference * 12;

    return {
      currentTotalInterest: Math.round(currentTotalInterest * 100) / 100,
      currentTotalRepayments: Math.round(currentTotalRepayments * 100) / 100,
      currentMonthlyPayment: Math.round(currentMonthlyPayment * 100) / 100,

      newLoanAmount: Math.round(newLoanAmount * 100) / 100,
      newMonthlyPayment: Math.round(newMonthlyPayment * 100) / 100,
      newTotalInterest: Math.round(newTotalInterest * 100) / 100,
      newTotalRepayments: Math.round(newTotalRepayments * 100) / 100,

      monthlyPaymentDifference: Math.round(monthlyPaymentDifference * 100) / 100,
      totalInterestSaved: Math.round(totalInterestSaved * 100) / 100,
      netSavings: Math.round(netSavings * 100) / 100,
      breakEvenMonths,
      yearlysSavings: Math.round(yearlysSavings * 100) / 100,
    };
  }, [inputs]);

  const updateInput = <K extends keyof RefinanceInputs>(key: K, value: RefinanceInputs[K]) => {
    setInputs((prev) => ({ ...prev, [key]: value }));
  };

  return { inputs, results, updateInput, setInputs };
}
